import { useCallback, useState } from "react";

// A small "pop" of an icon as functional feedback that a user action
// registered — a Save/Up Next toggle, a copy-to-clipboard tap, and the like.
//
// Drive it from the user's action, NOT by observing the resulting state. Some
// state (e.g. saved/queued) also flips false→true when a store hydrates from
// the server on load, and the glyph must not bounce on hydration; observing
// the value also couldn't tell a user action from a hydration flip, and a fast
// toggle-back could latch a timer-based flag on.
//
// Usage: call `pop()` from the action handler when it succeeds, and spread the
// returned `popKey` as BOTH the `key` and the `save-pop`-gate on the glyph:
//
//   const { popKey, pop } = useIconPop();
//   <BookmarkIcon key={popKey} className={cn(..., popKey > 0 && "save-pop")} />
//
// `key={popKey}` remounts the glyph on each pop so the finite CSS animation
// (`.save-pop` in globals.css, reduced-motion guarded) replays every time; the
// `popKey > 0` gate keeps the first (mount) render static so nothing animates
// until the user actually acts.
export function useIconPop(): { popKey: number; pop: () => void } {
  const [popKey, setPopKey] = useState(0);
  const pop = useCallback(() => setPopKey((k) => k + 1), []);
  return { popKey, pop };
}
